import { LAST_USED_ZOOM_USER_IDS_STORAGE_KEY } from 'src/contexts/LastUsedZoomUserIdsProvider';

import { AGENT_CHAT_SESSION_STORAGE_KEY } from 'src/views-new/AIAssistant/agentChatSessionStorage';

/**
 * Prefix of every LaunchDarkly bootstrap entry. The SDK writes `ld:<clientSideId>:<encoded context>`,
 * so the exact key changes with the evaluation context and cannot be listed here. The switch flow
 * removes the whole prefix instead of one key.
 */
const LAUNCH_DARKLY_STORAGE_KEY_PREFIX = 'ld:';

/**
 * The `localStorage` keys that hold data of one Organization. Each entry names why it is
 * Organization-scoped, because "clear this key" is otherwise unreviewable.
 */
const ORGANIZATION_SCOPED_STORAGE_KEYS = [
  // Zoom host ids come from the Organization's own Zoom integration. Another Organization's ids
  // are not valid hosts there.
  LAST_USED_ZOOM_USER_IDS_STORAGE_KEY,
  // Agent thread ids are rows of the Organization that created them. Restoring the dock after a
  // switch would ask the new Organization for threads it does not own.
  AGENT_CHAT_SESSION_STORAGE_KEY,
] as const;

/**
 * Removes every `localStorage` entry that belongs to the Organization the user is leaving.
 *
 * Called by `useSwitchActiveOrganization` between the switch mutation and the hard reload. The
 * reload is what resets the Apollo and react-query singletons; this helper only covers the state
 * that survives a reload because it lives in `localStorage`.
 *
 * Deliberately kept, because each is a property of the person or the device and not of the
 * Organization: `settings` (`SettingsContext`), `DISPLAY_TIMEZONE` (`DisplayTimezoneProvider`),
 * `MLOOP_DASHBOARD_COLOR_SCHEME` (`ColorSchemePreference`), `modernloop:last_activity_at`
 * (`sessionInactivity`), and the Auth0 token cache, which the switch flow re-mints rather than
 * drops. Clearing the Auth0 cache would log the user out instead of moving them.
 */
export const clearOrganizationScopedStorage = (): void => {
  try {
    ORGANIZATION_SCOPED_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));

    const launchDarklyKeys = Object.keys(window.localStorage).filter((key) =>
      key.startsWith(LAUNCH_DARKLY_STORAGE_KEY_PREFIX)
    );
    launchDarklyKeys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // localStorage can be disabled in private or locked-down browser contexts. The reload still
    // switches the Organization; only the stale entries survive.
  }
};
